import qwest from 'qwest';

import authApi from '../api/auth.jsx';

import events from '../lib/events.js';

import Branch from '../models/branch.jsx';

import EquipmentType from '../models/equipmentType.jsx';

import WaterTester from '../models/waterTester.jsx';

import debounce from 'lodash/debounce';


let sessionService;

class SessionService {
  constructor() {
    this._branches = {};
    this._branchSummaries = {};
    this._branchSummariesMine = {};
    this._customers = {};
    this._dealers = {};
    this._equipments = {};
    this._equipmentTypes = null;
    this._pools = {};
    this._products = null;
    this._user = null;
    this._waterTesters = {};
    this._search = {};
    this._isGrabbing = false;
    this._edittingTests = {}; // Store which test ids should display editable versions or not
    this._waterTestState = {};

    this._blockForAuth = false;
  }

  nuke() {
    sessionService = new SessionService();
  }

  getBranch(id) {
    return this._branches[id];
  }

  setBranch(branch) {
    this._branches[branch.id] = branch;
  }

  getFullBranches() {
    return Object.values(this._branches);
  }

  getMyBranches() {
    return Object.values(this._branchSummariesMine);
  }

  setFullBranches(branches) {
    branches.forEach(branch => {
      this._branches[branch.id] = branch;
    });
  }

  setBranches(branches) {
    branches.forEach(branch => {
      this._branchSummaries[branch.id] = branch;
    });
  }

  setMyBranches(branches) {
    branches.forEach(branch => {
      this._branchSummariesMine[branch.id] = branch;
    });
  }

  deleteBranch(branch) {
    delete this._branches[branch.id];
    delete this._branchSummaries[branch.id];
  }

  getUser() {
    if (this._user || this._blockForAuth || this._isGrabbing) {
      return this._user;
    }

    let self = this;

    this._isGrabbing = true;
    console.info('Tried to return user but no user was found');

    // User not loaded; we should do that
    debounce(authApi.getCurrentUser()
      .then(user => {
        if (!user) {
          return;
        }

        self.setUser(user);
        self._isGrabbing = false;
        try {
          window.appInsights.setAuthenticatedUserContext(user.email, user.dealerId, true);
        } catch (err) {
          console.error('Could not set user in application insights.');
          console.error(err);
        }
        let apis = [];
        apis.push(this.populateBranches());
        apis.push(this.populateWaterTesters());
        Promise.all(apis);
      })
      .catch(() => {
        this._blockForAuth = true;
        self._isGrabbing = false;
      }), 1000);
    return this._user;
  }


  downgrade(branchID) {
    authApi.downgrade(branchID)
      .then(user => {
        if (user) {
          this.setUser(user);
          let apis = [];
          //Apis.push(this.populateEquipmentTypes());

          apis.push(this.populateBranches());
          apis.push(this.populateWaterTesters());
          Promise.all(apis);
        }
      })
      .catch(err => {
        console.error(err);
      });
  }

  toggleTitrationTestMode() {
    authApi.toggleTitrationTestMode()
      .then(user => {
        if (user) {
          this.setUser(user);
          let apis = [];

          apis.push(this.populateBranches());
          apis.push(this.populateWaterTesters());
          Promise.all(apis);
          events.publish('dealeruser.toggleTitrationTestingOnly');
        }
      })
      .catch(err => {
        console.error(err);
      });
  }

  setUser(user) {
    this._user = user;
    events.publish('userset', user);
  }

  getProducts() {
    return this._products;
  }

  setProducts(products) {
    this._products = products;
  }

  getCustomer(id) {
    return this._customers[id];
  }

  setCustomer(id, cust) {
    this._customers[id] = cust;

    // Update source concern
    let ids = Object.keys(this._pools);

    for (let poolID of ids) {
      if (this._pools[poolID].customerID === cust.id) {
        this._pools[poolID].sourceConcern = cust.sourceConcern;
      }
    }
  }

  deleteCustomer(customer) {
    delete this._customers[customer.id];
  }

  getEquipmentTypes() {
    return this._equipmentTypes;
  }

  setEquipmentTypes(equipmentTypes) {
    this._equipmentTypes = equipmentTypes;
  }

  getEquipments(poolID) {
    return this._equipments[poolID];
  }

  setEquipments(equipments, poolID) {
    this._equipments[poolID] = equipments;
  }

  getPool(id) {
    return this._pools[id];
  }

  setPool(id, pool) {
    this._pools[id] = pool;
  }

  deletePool(pool) {
    delete this._pools[pool.id];

    // Also need to delete from pool summary in customer model
    let customer = this.getCustomer(pool.customerID);

    if (!customer || !customer.pools || !customer.pools.length) {
      return;
    }
    let index = customer.pools.findIndex(s => s && s.id === pool.id);

    if (index === -1) {
      return;
    }
    delete this._customers[pool.customerID].pools[index];
  }

  updatePoolSummaries(customerId, updater) {
    let summaries = this.getCustomer(customerId).pools;

    summaries.forEach(updater);
  }

  getWaterTester(id) {
    return this._waterTesters[id];
  }

  getWaterTesters() {
    return Object.values(this._waterTesters);
  }

  setWaterTesters(testers) {
    this._waterTesters = []; // Reset so that deleted testers are deleted

    testers.forEach(tester => {
      this._waterTesters[tester.id] = tester;
    });
  }

  setWaterTester(tester) {
    this._waterTesters[tester.id] = tester;
  }

  deleteWaterTester(tester) {
    delete this._waterTesters[tester.id];
  }

  setEdittingTests(systemID, analysisID) {
    console.log('Setting editting tests in session.');
    this._edittingTests[systemID] = analysisID;
  }

  removeEdittingTests(systemID) {
    console.log('Removing editting tests in session');
    delete this._edittingTests[systemID];
  }

  persistAnalysis(state) {
    if (state && state.analysis && state.analysis.poolID) {
      this._waterTestState[state.analysis.poolID] = state;
    }
  }

  forgetAnalysis(id) {
    console.log('forgetting analysis: ' + id);
    delete this._waterTestState[id];
  }

  forgetAnalysisIfManual(id) {
    let state = this._waterTestState[id];

    if (state && state.manualScan) {
      console.log('analysis is manual');
      this.forgetAnalysis(id);
    }
  }

  getAnalysis(id) {
    console.log('persisted analysis for id: ' + id + '; ' + !!this._waterTestState[id]);
    return this._waterTestState[id];
  }

  getEdittingTests(systemID) {
    return this._edittingTests[systemID];
  }

  getSearch() {
    return this._search;
  }

  popSearch() {
    let search = this.getSearch();

    this.clearSearch();
    return search;
  }

  setSearch(query, topic = '') {
    this._search = Object.assign({}, { query, topic });
  }

  clearSearch() {
    this._search = {};
  }

  isSignedIn() {
    return !!this._user;
  }

  invalidateSession() {
    this._branches = {};
    this._branchSummaries = {};
    this._branchSummariesMine = {};
    this._customers = {};
    this._dealers = {};
    this._equipments = {};
    this._equipmentTypes = null;
    this._pools = {};
    this._products = null;
    this._user = null;
    this._waterTesters = {};
    this._search = {};
    this._wentBack = false;
    events.publish('userset', this._user);
  }

  populateBranches() {
    return new Promise((resolve, reject) => {
      let branches = this.getFullBranches();

      if (branches.length > 0) {
        resolve(branches);
      } else {
        qwest.get('/Branch/EditableList')
          .then((xhr, response) => {
            if (response.success) {
              branches = response.data.map(x => Branch.fromApiFormat(x));
              this.setFullBranches(branches);
              resolve(branches);
            } else {
              reject(response);
            }
          })
          .catch(err => reject(err));

        qwest.get('/Branch/Mine')
          .then((xhr, response) => {
            if (response.success) {
              branches = response.data.map(x => Branch.fromApiFormat(x));
              this.setMyBranches(branches);
              resolve(branches);
            } else {
              reject(response);
            }
          })
          .catch(err => reject(err));
      }
    });
  }

  populateEquipmentTypes() {
    return new Promise((resolve, reject) => {
      let types = this.getEquipmentTypes();

      if (types) {
        resolve(types);
      } else {
        qwest.get('/Equipment/Types')
          .then((xhr, response) => {
            if (response.success) {
              types = response.data.map(et => EquipmentType.fromApiFormat(et));
              this.setEquipmentTypes(types);
              resolve(types);
            } else {
              reject(response);
            }
          });
      }
    });
  }

  populateWaterTesters() {
    return new Promise((resolve, reject) => {
      let testers = this.getWaterTesters();

      if (testers.length > 0) {
        resolve(testers);
      } else {
        qwest.get('/WaterTester/All')
          .then((xhr, response) => {
            if (response.success) {
              testers = response.data.map(x => WaterTester.fromApiFormat(x));
              this.setWaterTesters(testers);
              resolve(testers);
            } else {
              reject(response);
            }
          })
          .catch(err => reject(err));
      }
    });
  }
}

sessionService = new SessionService();

export default sessionService;
